import dayjs from 'dayjs';
import { ISO_DATE_FORMAT } from 'src/constants/constants';

export const PATHS = {
  ASSEMBLIES: '/assemblies',
  ASSEMBLY_ADD: '/assemblies/add',
  ASSEMBLY_EDIT: (id: string | number) => `/assemblies/edit/${id}`,
  ASSEMBLY_TAGS_ADD: '/assemblies/tags/add',
  ASSEMBLY_TAGS_EDIT: (id: string) => `/assemblies/tags/edit/${id}`,
  ASSEMBLY_MANAGE_TAGS: '/assemblies/tags',
  ASSEMBLIES_BULK_REPLACE: '/assemblies/bulk-replace',
  ASSEMBLY_DETAILS: (id: string | number) => `/assemblies/details/${id}`,
  ASSEMBLY_OVERRIDES: (
    locationCode: string,
    scheduleAssemblyId: string | number,
  ) => `/assemblies/${locationCode}/overrides/${scheduleAssemblyId}`,
  ASSEMBLY_LAYOUTS: '/assemblies/layouts',
  ASSEMBLY_LAYOUTS_ADD: '/assemblies/layouts/add',
  ASSEMBLY_LAYOUTS_EDIT: (id: string | number) =>
    `/assemblies/layouts/edit/${id}`,
  ASSEMBLY_LAYOUTS_CREATE_SECTIONS: (id: string | number) =>
    `/assemblies/layouts/create/${id}`,
  COMPONENTS: '/components',
  ASSEMBLY_LAYOUTS_CREATE_ROTATION_SECTIONS: (id: string | number) =>
    `/assemblies/layouts/create/rotation/${id}`,
  COMPONENTS_ID: (id: string | number) => `/components/${id}`,
  COMPONENT_ADD: '/components/add',
  COMPONENT_REPLACE: (id?: string | number) =>
    `/components/component-replace?componentId=${id}`,
  COMPONENT_DETAILS: (id?: string | number) =>
    `/components/details${id ? `/${id}` : ''}`,
  COMPONENT_EDIT: (id: string | number) => `/components/edit/${id}`,
  COMPONENT_BULK_REPLACE: (
    componentId: string | number,
    replaceComponentId?: string | number,
  ) => {
    const cleanComponentId = String(componentId).replace(/[{}]/g, '');
    const cleanReplaceComponentId = replaceComponentId
      ? String(replaceComponentId).replace(/[{}]/g, '')
      : undefined;

    return `/components/bulk-replace?componentId=${cleanComponentId}${
      cleanReplaceComponentId
        ? `&replaceComponentId=${cleanReplaceComponentId}`
        : ''
    }`;
  },
  COMPONENT_TAGS_ADD: '/components/tags/add',
  COMPONENT_TAGS_EDIT: (id: string) => `/components/tags/edit/${id}`,
  COMPONENT_TAGS_MANAGE: '/components/tags/manage',
  DEVTOOLS: '/devtools',
  DEVTOOLS_LAYOUTS: '/devtools/layouts',
  DEVTOOLS_STORE_TIME: '/devtools/storeTime',
  DEVTOOLS_ASSEMBLY_STATE: '/devtools/assemblyState',
  DEVTOOLS_WIDGET_PAGE: '/devtools/widgetPage',
  DEVTOOLS_PUBLICATIONS: '/devtools/publications',
  LOCATIONS: '/locations/locations',
  LOCATION_ADD: '/locations/add',
  LOCATION_GROUP: (locationGroupId: string) =>
    `/locations/group/${locationGroupId}`,
  LOCATION_GROUPS: '/locations/groups',
  LOCATION_GROUP_DETAILS: (locationGroupId: string) =>
    `/locations/group/${locationGroupId}/details`,
  LOCATION_GROUP_TIMINGS: (locationGroupId: string) =>
    `/locations/group/${locationGroupId}/timings`,
  LOCATION_GROUP_ASSIGN: (locationGroupId: string) =>
    `/locations/group/${locationGroupId}/assign`,
  LOCATION_GROUP_SCHEDULE: (locationGroupId: string) =>
    `/locations/group/${locationGroupId}/schedule`,
  LOCATION_GROUP_CALENDAR: (locationGroupId: string) =>
    `/locations/group/${locationGroupId}/calendar`,
  LOCATION: (locationCode: string) => `/locations/${locationCode}`,
  LOCATION_DETAILS: (locationCode: string) =>
    `/locations/${locationCode}/details`,
  LOCATION_TIMINGS: (locationCode: string, locationGroupId?: number | string) =>
    `/locations/${locationCode}/timings${
      locationGroupId ? `?groupID=${locationGroupId}` : ''
    }`,
  LOCATION_OVERRIDES: (locationCode: string) =>
    `/locations/${locationCode}/overrides`,
  LOCATION_ASSEMBLY_OVERRIDES_ADD: (
    locationCode: string,
    scheduleAssemblyId: string,
  ) => `/locations/${locationCode}/overrides/${scheduleAssemblyId}`,
  LOCATION_SCHEDULE: (locationCode: string) =>
    `/locations/${locationCode}/schedule`,
  LOCATION_CALENDAR: (locationCode: string) =>
    `/locations/${locationCode}/calendar`,
  LOCATION_TIME_MACHINE: (locationCode: string) =>
    `/locations/${locationCode}/time-machine`,
  LOCATIONS_SCHEDULE_COMPONENTS: '/locations/schedule-components',
  LOCATION_SCHEDULE_ALL: (locationCode: string, scheduleId: string | number) =>
    `/locations/${locationCode}/schedule/${scheduleId}`,
  LOCATIONS_SCHEDULE_OVERRIDE: (
    locationCode: string,
    scheduleId: string | number,
  ) => `/locations/${locationCode}/schedule/${scheduleId}/override`,
  LOCATIONS_TAGS: '/locations/tags',
  LOCATIONS_TAGS_ADD: '/locations/tags/add',
  LOCATIONS_TAGS_EDIT: (id: string) => `/locations/tags/edit/${id}`,
  LIST_PUBLICATIONS: '/locations/publications',
  ASSEMBLY_PUBLICATIONS: '/locations/assembly-publications',
  NOT_FOUND: '/not-found',
  OO_LOCATION_PAGE: '/locations',
  LOCATIONS_BULK_EDIT_MESSAGING: '/locations/bulk-edit-messaging',
  PRODUCTS: '/products',
  PRODUCT_ADD: '/products/add',
  PRODUCT_CATEGORIES: '/products/categories',
  PRODUCT_CATEGORIES_ADD: '/products/categories/add',
  PRODUCT_CATEGORIES_MANAGE: '/products/categories/manage',
  PRODUCT_CATEGORIES_EDIT: (id: string) => `/products/categories/edit/${id}`,
  PRODUCT_EDIT: (productCode: string) => `/products/edit/${productCode}`,
  PRODUCT_DETAILS: (productCode: string) => `/products/details/${productCode}`,
  PRODUCT_VARIATION_TEMPLATE_ADD: (
    productCode: string,
    variationCode: string,
  ) => `/products/edit/${productCode}/variations/${variationCode}/template/add`,
  PRODUCT_VARIATION_TEMPLATE_EDIT: (
    productCode: string,
    variationCode: string,
    templateId: string,
  ) =>
    `/products/edit/${productCode}/variations/${variationCode}/template/edit/${templateId}`,
  PRODUCT_PUBLICATIONS: `/locations/publications/products`,
  SETTINGS: '/settings',
  SETTINGS_CONFIGURATIONS: '/settings/configurations',
  SETTINGS_ACCESSIBILITY: '/settings/accessibility',
  SETTINGS_CUSTOMER_TRIGGERED_EVENTS: '/settings/customerTriggeredEvents',
  SETTINGS_ACTION_TRACKING: '/settings/actionTracking',
  SETTINGS_ABOUT: '/settings/about',
  UNASSIGNED_LOCATIONS: '/locations/unassigned',
  SCHEDULE_PREVIEW: (
    locationGroupId: string,
    locationCode?: string,
    daypartId?: string,
    date?: string,
    changeSetId?: string,
    position?: number,
  ) => {
    const params = new URLSearchParams(
      Object.fromEntries(
        Object.entries({
          groupID: locationGroupId,
          locationCode,
          daypartID: daypartId,
          date: date ?? dayjs().format(ISO_DATE_FORMAT),
          changeSetID: changeSetId,
          position,
        }).filter(([_, value]) => value !== undefined) as Array<string[]>,
      ),
    );

    return `/locations/preview?${params}`;
  },
  MENU_BOARD_MODIFICATION: (
    locationCode: string,
    channelName: string,
    locationGroupId?: string,
    daypartId?: string,
    date?: string,
    changeSetId?: string,
    position?: number,
  ) => {
    const params = new URLSearchParams(
      Object.fromEntries(
        Object.entries({
          groupID: locationGroupId,
          daypartID: daypartId,
          date: date ?? dayjs().format(ISO_DATE_FORMAT),
          changeSetID: changeSetId,
          position,
        }).filter(([_, value]) => value !== undefined) as Array<string[]>,
      ),
    );

    return `/locations/${locationCode}/details/${channelName}/menu-board-modification?${params}`;
  },
};
